import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { volunteersTableColumns } from './VolunteersData';
import { Button } from 'react-bootstrap';
// import axios from 'axios';
import { ServerPath } from '../../Constant';
import { H4 } from "../../AbstractElements";
// Import icons
import Search from '../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../assets/images/svg/search_iconSos.svg';
import Calendar from '../../assets/images/svg/Calendar_SoS.svg';
import Filter from '../../assets/images/svg/Filter_SoS.svg';
// Import date range picker
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import * as XLSX from 'xlsx';
import { _axios } from '../../utils/axios';

const PerformingVolunteersTable = () => {
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);

    // Search, filter and calendar states
    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const searchRef = useRef(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarRef = useRef(null);
    const [dateRange, setDateRange] = useState([
        {
            startDate: null,
            endDate: null,
            key: 'selection'
        }
    ]);

    const filterOptions = [
        { label: 'All', value: 'all' },
        { label: 'Gold Badge', value: 'Gold' },
        { label: 'Silver Badge', value: 'Silver' },
        { label: 'Bronze Badge', value: 'Bronze' },
        { label: 'No Badge', value: 'none' }
    ];
    const [showExcelDropdown, setShowExcelDropdown] = useState(false);
    const [showCsvDropdown, setShowCsvDropdown] = useState(false);
    const excelDropdownRef = useRef(null);
    const csvDropdownRef = useRef(null);

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearch(false);
            }
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
            // Add these new conditions
            if (excelDropdownRef.current && !excelDropdownRef.current.contains(event.target)) {
                setShowExcelDropdown(false);
            }
            if (csvDropdownRef.current && !csvDropdownRef.current.contains(event.target)) {
                setShowCsvDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await _axios.get(`${ServerPath}/referrals/performing-volunteers`);
            console.log('performing ', response);
            if (response.data.success) {
                const transformedData = response.data.data.map((item, index) => ({
                    device_id: item.device_id,
                    volunteer_name: item.volunteer_name || 'Unknown',
                    mobile_no: item.mobile_no,
                    download_count: item.download_count || 0,
                    active_users: item.active_users || 0,
                    badge: item.badge || 'No Badge',
                    location: item.location || '-',
                    created_date: item.created_date
                }));
                setData(transformedData);
                setOriginalData(transformedData);
                setTotalRows(transformedData.length);
            }
        } catch (error) {
            console.error('Error fetching performing volunteers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, perPage]);

    // Search and filter functions
    const handleSearch = (value) => {
        setSearchValue(value);
        const currentDateRange = dateRange[0]?.startDate && dateRange[0]?.endDate ? dateRange[0] : null;
        applyFiltersAndSearch(value, selectedFilter, currentDateRange);
    };

    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        const currentDateRange = dateRange[0]?.startDate && dateRange[0]?.endDate ? dateRange[0] : null;
        applyFiltersAndSearch(searchValue, value, currentDateRange);
        setShowDropdown(false);
    };

    const handleDateRangeChange = (item) => {
        setDateRange([item.selection]);
        applyFiltersAndSearch(searchValue, selectedFilter, item.selection);
    };

    const clearDateFilter = () => {
        const clearedRange = {
            startDate: null,
            endDate: null,
            key: 'selection'
        };
        setDateRange([clearedRange]);
        applyFiltersAndSearch(searchValue, selectedFilter, null);
        setShowCalendar(false);
    };

    const applyFiltersAndSearch = (searchValue, filterValue, dateValue) => {
        let filteredData = [...originalData];

        // Apply search filter
        if (searchValue && searchValue.trim()) {
            const searchTerm = searchValue.toLowerCase();
            filteredData = filteredData.filter(item => {
                return (
                    String(item.volunteer_name || '').toLowerCase().includes(searchTerm) ||
                    String(item.mobile_no || '').toLowerCase().includes(searchTerm) ||
                    String(item.location || '').toLowerCase().includes(searchTerm) ||
                    String(item.badge || '').toLowerCase().includes(searchTerm)
                );
            });
        }

        // Apply badge filter
        if (filterValue && filterValue !== 'all') {
            filteredData = filteredData.filter(item => {
                if (filterValue === 'none') {
                    return !item.badge || item.badge === 'none';
                }
                return item.badge === filterValue;
            });
        }

        // Apply date filter
        if (dateValue?.startDate && dateValue?.endDate) {
            filteredData = filteredData.filter(item => {
                const createdDate = new Date(item.created_date);
                const startDate = new Date(dateValue.startDate);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(dateValue.endDate);
                endDate.setHours(23, 59, 59, 999);
                return createdDate >= startDate && createdDate <= endDate;
            });
        }

        setData(filteredData);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    // Utility function to format data for export
    const formatDataForExport = (dataToExport) => {
        return dataToExport.map((item, index) => ({
            'Sr. No.': index + 1,
            'Device ID': item.device_id || '',
            'Volunteer Name': item.volunteer_name || '',
            'Mobile Number': item.mobile_no || '',
            'Download Count': item.download_count || 0,
            'Active Users': item.active_users || 0,
            'Badge': item.badge || 'No Badge',
            'Location': item.location || '',
            'Created Date': item.created_date ? new Date(item.created_date).toLocaleDateString('en-IN') : ''
        }));
    };

    // Generate filename with current date
    const generateFilename = (prefix) => {
        const today = new Date();
        const dateString = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        return `${prefix}_${dateString}`;
    };

    // Export to Excel function
    const exportToExcel = (dataToExport, isAllData = false) => {
        try {
            const formattedData = formatDataForExport(dataToExport);
            const filename = generateFilename(isAllData ? 'Referrals_All_Data' : 'Referrals_Filtered_Data');

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(formattedData);

            // Set column widths
            const colWidths = [
                { wch: 8 },   // Sr. No.
                { wch: 15 },  // Device ID
                { wch: 20 },  // Volunteer Name
                { wch: 15 },  // Mobile Number
                { wch: 12 },  // Download Count
                { wch: 12 },  // Active Users
                { wch: 12 },  // Badge
                { wch: 20 },  // Location
                { wch: 15 }   // Created Date
            ];
            ws['!cols'] = colWidths;

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Performing Volunteers');

            // Add metadata sheet
            const metaData = [
                ['Export Information'],
                ['Export Date', new Date().toLocaleString('en-IN')],
                ['Total Records', formattedData.length],
                ['Export Type', isAllData ? 'All Data' : 'Filtered Data']
            ];
            const metaWs = XLSX.utils.aoa_to_sheet(metaData);
            XLSX.utils.book_append_sheet(wb, metaWs, 'Export Info');

            // Save file
            XLSX.writeFile(wb, `${filename}.xlsx`);

            console.log(`Excel export completed: ${formattedData.length} records`);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            alert('Failed to export Excel file. Please try again.');
        }
    };

    // Export to CSV function
    const exportToCsv = (dataToExport, isAllData = false) => {
        try {
            const formattedData = formatDataForExport(dataToExport);
            const filename = generateFilename(isAllData ? 'Referrals_All_Data' : 'Referrals_Filtered_Data');

            // Convert to CSV
            const ws = XLSX.utils.json_to_sheet(formattedData);
            const csvData = XLSX.utils.sheet_to_csv(ws);

            // Create and download file
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');

            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `${filename}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            console.log(`CSV export completed: ${formattedData.length} records`);
        } catch (error) {
            console.error('Error exporting to CSV:', error);
            alert('Failed to export CSV file. Please try again.');
        }
    };

    // Export handlers
    const handleExcelExport = (exportAll = false) => {
        const dataToExport = exportAll ? originalData : data;
        exportToExcel(dataToExport, exportAll);
        setShowExcelDropdown(false);
    };

    const handleCsvExport = (exportAll = false) => {
        const dataToExport = exportAll ? originalData : data;
        exportToCsv(dataToExport, exportAll);
        setShowCsvDropdown(false);
    };

    return (
        <Fragment>
            <Container fluid={true}>
                <Card>
                    <CardHeader>
                        <div className="d-flex justify-content-between align-items-center">
                            <H4>All Referrer Data</H4>
                            <div className="d-flex align-items-center gap-3">
                                {/* Search Component */}
                                {/* <div className="position-relative" ref={searchRef}>
                                    <button
                                        className="bg-transparent border-0"
                                        onClick={() => setShowSearch(true)}
                                    >
                                        <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
                                    </button>
                                    {showSearch && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '45px',
                                                left: '-180px',
                                                width: '220px',
                                                zIndex: 1000
                                            }}
                                        >
                                            <div
                                                style={{
                                                    background: 'white',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                    padding: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        background: '#F8F7FA',
                                                        borderRadius: '6px',
                                                        padding: '6px 12px'
                                                    }}
                                                >
                                                    <input
                                                        type="text"
                                                        placeholder="Search volunteers..."
                                                        value={searchValue}
                                                        onChange={(e) => handleSearch(e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            fontSize: '14px',
                                                            color: '#666',
                                                            outline: 'none'
                                                        }}
                                                        autoFocus
                                                    />
                                                    <button
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            padding: 0,
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                        onClick={() => handleSearch(searchValue)}
                                                    >
                                                        <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div> */}

                                {/* Calendar Component */}
                                {/* <div className="position-relative" ref={calendarRef}>
                                    <button
                                        className="bg-transparent border-0"
                                        onClick={() => setShowCalendar(!showCalendar)}
                                    >
                                        <img src={Calendar} alt="calendar" style={{ width: '36px', height: '36px' }} />
                                    </button>
                                    {showCalendar && (
                                        <div
                                            className="position-absolute end-0 mt-2 bg-white rounded shadow p-3"
                                            style={{
                                                width: 'auto',
                                                maxWidth: '300px',
                                                zIndex: 1000,
                                                right: 0
                                            }}
                                        >
                                            <DateRange
                                                editableDateInputs={true}
                                                onChange={handleDateRangeChange}
                                                moveRangeOnFirstSelection={false}
                                                ranges={dateRange}
                                                maxDate={new Date()}
                                                months={1}
                                                direction="horizontal"
                                                className="date-range"
                                                staticRanges={[]}
                                                inputRanges={[]}
                                                rangeColors={['#0066ff']}
                                                minDate={new Date("2000-01-01")}
                                                showSelectionPreview={true}
                                                showMonthAndYearPickers={true}
                                                monthDisplayFormat="MMMM yyyy"
                                                dayDisplayFormat="d"
                                                showDateDisplay={false}
                                                monthsShown={1}
                                            />
                                            <div className="d-flex justify-content-end mt-2">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={clearDateFilter}
                                                >
                                                    Clear
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div> */}

                                {/* Filter Component */}
                                {/* <div className="position-relative" ref={dropdownRef}>
                                    <button
                                        className="bg-transparent border-0"
                                        onClick={() => setShowDropdown(!showDropdown)}
                                    >
                                        <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
                                    </button>
                                    {showDropdown && (
                                        <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-2"
                                            style={{ width: '200px', zIndex: 1000 }}>
                                            {filterOptions.map((option) => (
                                                <div key={option.value} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        name="filterOption"
                                                        id={`filter-${option.value}`}
                                                        checked={selectedFilter === option.value}
                                                        onChange={() => handleFilterChange(option.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`filter-${option.value}`}>
                                                        {option.label}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div> */}

                                {/* Export Buttons */}
                                {/* Replace the existing Export Buttons section with this */}
                                {/* Excel Export Dropdown */}
                                <div className="position-relative" ref={excelDropdownRef}>
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => setShowExcelDropdown(!showExcelDropdown)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                    >
                                        Export Excel
                                        <span style={{ fontSize: '10px' }}>▼</span>
                                    </Button>
                                    {showExcelDropdown && (
                                        <div
                                            className="position-absolute bg-white rounded shadow-sm border"
                                            style={{
                                                top: '100%',
                                                right: 0,
                                                zIndex: 1000,
                                                minWidth: '150px',
                                                marginTop: '4px'
                                            }}
                                        >
                                            <button
                                                className="w-100 text-start px-3 py-2 border-0 bg-transparent hover-bg-light"
                                                style={{ fontSize: '14px' }}
                                                onClick={() => handleExcelExport(false)}
                                            >
                                                Current View ({data.length} records)
                                            </button>
                                            <hr className="m-0" />
                                            <button
                                                className="w-100 text-start px-3 py-2 border-0 bg-transparent hover-bg-light"
                                                style={{ fontSize: '14px' }}
                                                onClick={() => handleExcelExport(true)}
                                            >
                                                All Data ({originalData.length} records)
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* CSV Export Dropdown */}
                                <div className="position-relative" ref={csvDropdownRef}>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => setShowCsvDropdown(!showCsvDropdown)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                                    >
                                        Export CSV
                                        <span style={{ fontSize: '10px' }}>▼</span>
                                    </Button>
                                    {showCsvDropdown && (
                                        <div
                                            className="position-absolute bg-white rounded shadow-sm border"
                                            style={{
                                                top: '100%',
                                                right: 0,
                                                zIndex: 1000,
                                                minWidth: '150px',
                                                marginTop: '4px'
                                            }}
                                        >
                                            <button
                                                className="w-100 text-start px-3 py-2 border-0 bg-transparent hover-bg-light"
                                                style={{ fontSize: '14px' }}
                                                onClick={() => handleCsvExport(false)}
                                            >
                                                Current View ({data.length} records)
                                            </button>
                                            <hr className="m-0" />
                                            <button
                                                className="w-100 text-start px-3 py-2 border-0 bg-transparent hover-bg-light"
                                                style={{ fontSize: '14px' }}
                                                onClick={() => handleCsvExport(true)}
                                            >
                                                All Data ({originalData.length} records)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <DataTable
                            columns={volunteersTableColumns}
                            data={data}
                            pagination
                            paginationServer={false}
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            progressPending={loading}
                            highlightOnHover
                            striped
                        />
                    </CardBody>
                </Card>
            </Container>
        </Fragment>
    );
};

export default PerformingVolunteersTable;